import type { ISessionDBEntry } from '@bugfender/sdk/lib/data/sessions.table'

/**
 * The part of the Bugfender SDK 2.4.0 upload loop (`StorageProcessor`) the guard replaces. These members are
 * protected in the SDK's typings; `run()` looks each one up on the instance every time it runs, so replacing them
 * also reaches the runs its 15-second interval starts.
 */
interface UploadLoop {
	registerSession(session: ISessionDBEntry): Promise<ISessionDBEntry>
	sendIssues(session: ISessionDBEntry): Promise<boolean>
	sendLogs(session: ISessionDBEntry): Promise<void>
	cleanupSessions(sessions: ISessionDBEntry[]): Promise<void>
	readonly sessionsTable: SessionsTable
}

interface SessionsTable {
	getAll(): Promise<ISessionDBEntry[]>
}

const isFunction = (value: unknown): boolean => typeof value === 'function'

const isUploadLoop = (value: unknown): value is UploadLoop => {
	const loop = value as Partial<UploadLoop> | null | undefined

	return (
		isFunction(loop?.registerSession) &&
		isFunction(loop?.sendIssues) &&
		isFunction(loop?.sendLogs) &&
		isFunction(loop?.cleanupSessions) &&
		isFunction(loop?.sessionsTable?.getAll)
	)
}

const isRegistered = (session: ISessionDBEntry): boolean => Boolean(session.id)

const guardUploadLoop = (loop: unknown): void => {
	if (!isUploadLoop(loop)) return

	const { registerSession, sendIssues, sendLogs, cleanupSessions, sessionsTable } = loop
	const { getAll } = sessionsTable

	loop.registerSession = (session) => registerSession.call(loop, session).catch(() => session)
	loop.sendIssues = (session) =>
		isRegistered(session) ? sendIssues.call(loop, session) : Promise.resolve(false)
	loop.sendLogs = (session) =>
		isRegistered(session) ? sendLogs.call(loop, session) : Promise.resolve()
	loop.cleanupSessions = (sessions) => cleanupSessions.call(loop, sessions).catch(() => undefined)
	sessionsTable.getAll = () => getAll.call(sessionsTable).catch(() => [])
}

/**
 * Keeps the Bugfender SDK uploading after a failed upload round.
 *
 * SDK 2.4.0 (and 4.0.1, the latest checked) marks its upload loop busy at the start of each round and clears the
 * flag only when the round succeeds. When registering the page's session fails (`POST /session` answered with
 * anything but 200 or 304, e.g. the page closing, Safari suspending it, or a dropped connection) or a storage read
 * is aborted, the loop stays busy for the life of the page: no more logs, crashes or the closing flush until the
 * guest opens the page again.
 *
 * Here those failures end the round instead, so the next round, 15 seconds later, registers the session again and
 * then sends what was kept. Until the session is registered nothing is sent for it: the SDK would post without a
 * session id and delete what it posted. Failures are not logged, since that is the empty error line they used to
 * leave. If the SDK no longer has this shape, nothing is changed.
 *
 * Call it before `init`: the SDK builds the loop midway through init and its first round can start before init
 * resolves, so the loop is guarded the moment the SDK stores it as `processor`.
 *
 * Remove when the SDK clears the flag on failure; see "Vendor workarounds" in CLAUDE.md.
 */
export const guardBugfenderProcessor = (sdk: object): void => {
	const { processor } = sdk as { readonly processor?: unknown }

	if (processor !== undefined) return guardUploadLoop(processor)

	Object.defineProperty(sdk, 'processor', {
		configurable: true,
		set: (built: unknown) => {
			Object.defineProperty(sdk, 'processor', {
				value: built,
				writable: true,
				enumerable: true,
				configurable: true,
			})
			guardUploadLoop(built)
		},
	})
}
